import usePrevious from 'hooks/usePrevious';
import { isEqual } from 'lodash';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ADD_DELETE_REQUEST, UPDATE_APPLICATION_DETAIL_POST_DATA } from 'store/key';
import { generateRandomId, getTimeWithTimeZone, produce } from 'utility/general';

const useSitePIData = ({ formId = -1 }) => {
    const [openPopup, setOpenPopup] = useState(false);
    const [selectedRow, setSelectedRow] = useState({});
    const serverTableKey = 'pi';
    const updatePostData = useSelector((state) => state.applicationDetailPostData.postData[serverTableKey]);
    const remoteData = useSelector((state) => state.applicationDetail?.[formId]?.[serverTableKey]);
    const dispatch = useDispatch();
    const [isEdit, setEdit] = useState(false);

    const [rows, setRows] = useState(remoteData ?? []);
    const previousData = usePrevious(remoteData);

    // update row when remote data update
    useEffect(() => {
        if (!isEqual(previousData, remoteData)) {
            setRows(remoteData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [remoteData]);

    const handleEditClick = (row) => {
        setEdit(true);
        setSelectedRow(row);
        setOpenPopup(true);
    };

    const triggerFormCreate = async (id) => {
        setEdit(false);
        setSelectedRow({});
        setOpenPopup(true);
    };

    const onRecordUpdate = async (data) => {
        setRows(
            produce((draft) => {
                data.id = `temp_${generateRandomId()}`;
                draft.push(data);
            }),
        );
    };

    const handleEditAfterCreation = (row) => {
        let postData = updatePostData ?? [];

        const index = postData.findIndex((item) => {
            return item.id === row.id;
        });

        if (index === -1) {
            postData.push(row);
        } else {
            postData[index] = row;
        }

        dispatch({
            type: UPDATE_APPLICATION_DETAIL_POST_DATA,
            payload: {
                type: serverTableKey,
                data: postData,
                applicationFormId: formId,
            },
        });

        //update local data
        setRows(
            produce((draft) => {
                const index = draft.findIndex((item) => {
                    return item.id === row.id;
                });

                draft[index] = row;
            }),
        );
    };

    const handleCreateAfterCreation = async (data) => {
        data.id = `temp_${generateRandomId()}`;
        const newRows = produce(rows, (draft) => {
            draft.push(data);
        });
        setRows(newRows);
        dispatch({
            type: UPDATE_APPLICATION_DETAIL_POST_DATA,
            payload: {
                type: serverTableKey,
                data: [...(updatePostData ?? []), data],
                applicationFormId: formId,
            },
        });
    };

    const handleDeleteAfterCreation = async (row) => {
        setRows(
            produce((draft) => {
                const index = draft.findIndex((item) => {
                    return item.id === row.id;
                });

                if (index !== -1) {
                    draft.splice(index, 1);
                }
            }),
        );

        // skip if id starts with temp_, e.g. not exist in server
        if (row.id.toString().startsWith('temp_')) {
            let postData = updatePostData ?? [];

            const index = postData.findIndex((item) => {
                return item.id === row.id;
            });

            if (index !== -1) {
                postData.splice(index, 1);
            }

            dispatch({
                type: UPDATE_APPLICATION_DETAIL_POST_DATA,
                payload: {
                    type: serverTableKey,
                    data: postData,
                    applicationFormId: formId,
                },
            });

            return;
        }

        dispatch({
            type: ADD_DELETE_REQUEST,
            payload: {
                type: serverTableKey,
                id: row.id,
                applicationFormId: formId,
            },
        });
    };

    const [columns] = useState([
        { field: 'email', headerName: 'Email', width: 200 },
        { field: 'title', headerName: 'Title', width: 130, tooltip: 'e.g. Dr/Mr/Mrs/Ms/Prof.' },
        {
            field: 'surname',
            headerName: 'Surname',
            width: 120,
            valueGetter: (params) => params.getValue('surname'),
        },
        {
            field: 'firstName',
            headerName: 'First name',
            width: 120,
            valueGetter: (params) => params.getValue('firstName'),
        },
        { field: 'cluster', headerName: 'Cluster', width: 100 },
        {
            field: 'institution',
            headerName: 'Hospital / Institution',
            // tooltip: (
            //     <div>
            //         <div>Central IRB: Site PI shall be an employee/appointee of HA</div>
            //         <div style={{ marginTop: 15 }}>
            //             HKU/HA HKW IRB: Site PI shall be an employee/appointee/student of the HKU or HA HKWC
            //         </div>
            //         <div style={{ marginTop: 15 }}>
            //             Joint CUHK-NTEC CREC: Site PI shall be an employee/appointee/student of the CUHK or HA NTEC
            //         </div>
            //     </div>
            // ),
            width: 120,
        },
        {
            field: 'department',
            headerName: 'Department',
            width: 160,
        },
        {
            field: 'endEffectiveDate',
            headerName: 'End effective date',
            width: 150,
            valueGetter: (params) => {
                if (params.value) {
                    return getTimeWithTimeZone(params.value).format('YYYY-MM-DD');
                }

                return '-';
            },
        },
    ]);

    return {
        columns,
        rows,
        openPopup,
        setOpenPopup,
        selectedRow,
        triggerFormCreate,
        onRecordUpdate,
        handleEditClick,
        setRows,
        handleDeleteAfterCreation,
        handleCreateAfterCreation,
        handleEditAfterCreation,
        isEdit,
    };
};

export default useSitePIData;
